/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { ContentSetup, ContentType, ModuleInfo, PageDescription } from '.';
import {
  getProblemResult,
  PageType,
  ProblemStage,
  RawPageData
} from 'its-pages';
import { problemLoaded, setStage } from '../redux/actions/ui';
import { Dispatch } from '@reduxjs/toolkit';

export type MouseEventFunction = (e: React.MouseEvent | undefined) => void;
export type TimeoutType = ReturnType<typeof setTimeout> | undefined;

/**
 * creates a promise to sleep the given milliseconds
 *
 * @export
 * @param {number} [milliseconds=0]
 * @returns
 */
export function sleep(milliseconds = 0): Promise<void> {
  return new Promise((resolve): number =>
    window.setTimeout(resolve, milliseconds)
  );
}

export interface ScreenDimensions {
  width: number;
  height: number;
}

export function getScreenDimensions(): ScreenDimensions {
  return {
    width: Math.max(
      document.documentElement.clientWidth,
      window.innerWidth || 0
    ),
    height: Math.max(
      document.documentElement.clientHeight,
      window.innerHeight || 0
    )
  };
}

// mean - point around which the bell exists
// offset - maximum distance from the mean from which values are returned.
export function randomNormalRange(mean: number, offset: number): number {
  function randomNormal(): number {
    return (Math.random() + Math.random()) / 2;
  }

  const norm = randomNormal(); // get the bell value (0-1)
  const range = offset * 2; // get the total range of values
  const base = mean - range / 2; // get the begining of the range
  const ret = base + norm * range; // to the base add the range * the bell value
  return ret;
}

// mediam - point around which generate values
// offset - maximum distance from the mean from which values are returned.
export function randomRange(median: number, offset: number): number {
  const range = offset * 2; // get the total range of values
  const base = median - range / 2; // get the begining of the range
  const ret = base + Math.round(Math.random() * range); // to the base add the range * the bell value
  return ret;
}

export function getPageDescription(
  moduleInfo: ModuleInfo,
  progressionIndex: number
): PageDescription {
  const progression = moduleInfo.progression;
  const pageDescription = progression[progressionIndex];

  return pageDescription;
}

export function getPageDescriptionFromSetup(
  setup: ContentSetup
): PageDescription {
  return getPageDescription(setup.moduleInfo, setup.progressionIndex);
}

/**
 * 
 * @param setup 
 * @returns The broad question type/category that should be picked based on the setup context
 */
export function getContentTypeFromSetup(setup: ContentSetup): ContentType {
  return getPageDescriptionFromSetup(setup).contentType;
}

export function getContentType(
  moduleInfo: ModuleInfo,
  progressionIndex: number
): ContentType {
  return getPageDescription(moduleInfo, progressionIndex).contentType;
}

/**
 * 
 * @param setup 
 * @returns The kind of the page/screen that should be picked based on the setup context.
 *     Right now this is whether we should show a question or an info screen.
 */
export function getPageTypeFromSetup(setup: ContentSetup): PageType {
  return getPageDescriptionFromSetup(setup).pageType;
}

export const ANNOTATION_USE_SELECT = false;

export function isFireFox(): boolean {
  return navigator.userAgent.toLocaleLowerCase().indexOf('firefox') > -1;
}

export async function asyncForEach<T>(
  array: T[],
  callback: (item: T, index: number) => void
): Promise<void> {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index);
  }
}

export function safelyUnpackJson(json: string): any {
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

export const loadProblemLog = async (dispatch: Dispatch, _id: string | null) => {
  if (_id) {
    const logResult = await getProblemResult({
      time: _id
    });

    if (logResult) {
      const rawPageData = logResult?.eventData as RawPageData;
      dispatch(problemLoaded(rawPageData));
      dispatch(setStage(ProblemStage.ShowResult));
    }
  }
};
